import { Component, OnInit, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { first } from 'rxjs/operators';
import { AuthService } from '../../service/auth/auth.service';
import { environment } from '../../../environments/environment';
import { ReqResService } from '../../service/req-res.service';

declare var toastr: any;
declare var grecaptcha: any;

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.css']
})
export class LoginComponent implements OnInit, OnDestroy {

  forgotPasswordForm: FormGroup;
  forgotSubmitted = false;
  loginStep: 'email' | 'password' | 'newPassword' | 'forgotPassword' | 'signUp' = 'email';
  checkedEmail = '';

  signUpForm: FormGroup;
  signUpSubmitted = false;

  segmentObj: any[] = [];
  subSegmentObj: any[] = [];
  ssubSegmentObj: any[] = [];


  spinner = false;
  errorPassword = '';
  userPasswordChangeForceForm: FormGroup;

  loading = false;
  loginForm: FormGroup;
  submitted = false;
  returnUrl: string;
  errorMessage: string;
  accountNotFound = false;
  siteKey = environment.recaptchaSiteKey;
  showCaptcha = false;

  constructor(
    private titleService: Title,
    private formBuilder: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private reqRes: ReqResService
  ) { }

  ngOnInit() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('userinfo');

    this.titleService.setTitle('VMI Store | Login');

    this.loginForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]],
      password: [''],
      recaptchaToken: ['']
    });

    this.userPasswordChangeForceForm = this.formBuilder.group({
      ope: [3],
      newPassword: [
        '',
        [
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(/^(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/)
        ]
      ],
      confirmPassword: ['', Validators.required]
    });

    (window as any).onCaptchaSuccess = (token: string) => {
      this.loginForm.patchValue({
        recaptchaToken: token
      });
      this.loginForm.get('recaptchaToken')?.updateValueAndValidity();
    };

    (window as any).onCaptchaExpired = () => {
      this.loginForm.patchValue({
        recaptchaToken: ''
      });
      this.loginForm.get('recaptchaToken')?.updateValueAndValidity();
    };


    this.forgotPasswordForm = this.formBuilder.group({
      email: ['', [Validators.required, Validators.email]]
    });

    this.signUpForm = this.formBuilder.group({
      name: ['', Validators.required],
      surname: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      segmentId: [0, Validators.required],
      subSegmentId: [0, Validators.required],
      ssubSegmentId: [0, Validators.required]
    });
  }

  ngOnDestroy(): void {
    delete (window as any).onCaptchaSuccess;
    delete (window as any).onCaptchaExpired;
  }

  get loginFormControl() {
    return this.loginForm.controls;
  }

  setCaptchaValidation(): void {
    const captchaControl = this.loginForm.get('recaptchaToken');
    if (!captchaControl) return;

    if (this.showCaptcha) {
      captchaControl.setValidators([Validators.required]);
    } else {
      captchaControl.clearValidators();
    }

    captchaControl.updateValueAndValidity();
  }

  resetCaptcha(): void {
    this.loginForm.patchValue({ recaptchaToken: '' });
    this.loginForm.get('recaptchaToken')?.updateValueAndValidity();

    if (!this.showCaptcha) {
      return;
    }

    try {
      const w = window as any;
      const hasClients =
        w.___grecaptcha_cfg &&
        w.___grecaptcha_cfg.clients &&
        Object.keys(w.___grecaptcha_cfg.clients).length > 0;

      if (typeof grecaptcha !== 'undefined' && hasClients) {
        grecaptcha.reset();
      }
    } catch (e) {
      console.log('reCAPTCHA reset skipped:', e);
    }
  }

  onSubmit() {


    if (this.loginStep !== 'password') {
      return;
    }

    this.loginForm.get('password')?.setValidators([Validators.required]);
    this.loginForm.get('password')?.updateValueAndValidity();

    this.submitted = true;
    this.errorMessage = '';

    this.setCaptchaValidation();

    if (this.loginForm.invalid) {
      return;
    }

    this.loading = true;
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/';

    this.authService.login(this.loginForm.value)
      .pipe(first())
      .subscribe(
        data => {
          if (data.islogin == 1) {
            let _isNotAuthencatedUrl = this.authService.isNotAuthencatedUrl.replace("/", "");
            if (_isNotAuthencatedUrl != "login" &&
              !_isNotAuthencatedUrl.includes("reset-password") &&
              _isNotAuthencatedUrl != "forgot-password" &&
              _isNotAuthencatedUrl != "error403") {
              this.router.navigate([this.authService.isNotAuthencatedUrl]);
            } else {
              this.router.navigate([returnUrl]);
            }
          } else {
            this.errorMessage = data.error || 'Login failed';
            this.accountNotFound = data.accountNotFound === true;

            if (data.requireCaptcha) {
              this.showCaptcha = true;
              this.setCaptchaValidation();
              this.resetCaptcha();
            }

            this.loginForm.setErrors({
              invalidLogin: true
            });
          }

          this.loading = false;
        },
        err => {
          toastr.error('Oops! Something went wrong', '', { timeOut: 1000 });
          console.log(err);
          this.resetCaptcha();
          this.loading = false;
        }
      );
  }

  passwordSubmitted = false;
  onSubmitForce() {
    this.passwordSubmitted = true;
    this.errorPassword = '';

    if (this.userPasswordChangeForceForm.invalid ||
      this.userPasswordChangeForceForm.value.newPassword !=
      this.userPasswordChangeForceForm.value.confirmPassword) {
      return;
    }

    this.spinner = true;

    const formValue = {
      ...this.userPasswordChangeForceForm.value,
      email: this.checkedEmail
    };

    this.authService.setFirstPassword(formValue).subscribe(data => {
      this.spinner = false;

      if (data.success) {
        toastr.success("Password changed successfully. Please login.", '', { timeOut: 1000 });

        this.loginStep = 'password';

        this.loginForm.patchValue({
          email: this.checkedEmail,
          password: ''
        });

        this.resetForcePasswordForm();
        return;
      }

      if (data.alreadyUpdated) {
        toastr.warning(data.error, '', { timeOut: 2000 });

        this.loginStep = 'password';

        this.loginForm.patchValue({
          email: this.checkedEmail,
          password: ''
        });

        this.resetForcePasswordForm();
        return;
      }

      this.errorPassword = data.error || 'Password could not be updated.';
      this.resetForcePasswordForm();

    }, error => {
      this.spinner = false;
      toastr.error("Oops! Something went wrong", '', { timeOut: 1000 });
      this.resetForcePasswordForm();
    });
  }
  resetForcePasswordForm() {
    this.userPasswordChangeForceForm.patchValue({
      newPassword: '',
      confirmPassword: ''
    });

    this.userPasswordChangeForceForm.markAsUntouched();
    this.userPasswordChangeForceForm.markAsPristine();
  }
  checkEmail() {
    this.submitted = true;
    this.errorMessage = '';
    this.accountNotFound = false;
    this.loginForm.setErrors(null);

    const email = this.loginForm.value.email;

    if (!email || !email.trim()) {
      return;
    }

    this.loading = true;

    this.authService.checkLoginEmail({ email: email })
      .pipe(first())
      .subscribe(data => {
        this.loading = false;
        if (!data.success) {
          this.accountNotFound = data.accountNotFound === true;
          this.errorMessage = data.error || 'We could not find an account with this email. Please sign up to request access.';
          this.loginForm.setErrors({ invalidLogin: true });
          return;
        }
        this.accountNotFound = false;
        this.loginForm.setErrors(null);
        this.checkedEmail = email;

        if (!data.requirePasswordChange) {
          this.loginStep = 'newPassword';
        } else {
          this.loginStep = 'password';
        }
      }, err => {
        this.loading = false;
        toastr.error('Oops! Something went wrong', '', { timeOut: 1000 });
      });
  }


  hasForcePasswordNumber(): boolean {
    return /[0-9]/.test(this.userPasswordChangeForceForm?.value?.newPassword || '');
  }

  hasForcePasswordSymbol(): boolean {
    return /[^A-Za-z0-9]/.test(this.userPasswordChangeForceForm?.value?.newPassword || '');
  }

  hasForcePasswordUppercase(): boolean {
    return /[A-Z]/.test(this.userPasswordChangeForceForm?.value?.newPassword || '');
  }

  onPasswordForceKeyup() {
    this.errorPassword = '';
  }



  backToEmailStep() {
    this.loginStep = 'email';
    this.checkedEmail = '';
    this.errorPassword = '';
    this.passwordSubmitted = false;
    this.accountNotFound = false;
    this.errorMessage = '';
    this.loginForm.setErrors(null);

    this.userPasswordChangeForceForm.reset({
      ope: 3,
      newPassword: '',
      confirmPassword: ''
    });

    this.loginForm.patchValue({
      password: ''
    });

    this.loginForm.get('password')?.clearValidators();
    this.loginForm.get('password')?.updateValueAndValidity();
  }



  showForgotPassword() {
    this.loginStep = 'forgotPassword';
    this.forgotSubmitted = false;
    this.errorMessage = '';
    this.accountNotFound = false;
    this.loginForm.setErrors(null);

    this.forgotPasswordForm.patchValue({
      email: this.loginForm.value.email || ''
    });
  }

  submitForgotPassword() {
    this.forgotSubmitted = true;
    this.errorMessage = '';

    if (this.forgotPasswordForm.invalid) {
      return;
    }

    this.loading = true;

    this.authService.forgotPassword(this.forgotPasswordForm.value)
      .pipe(first())
      .subscribe(data => {
        this.loading = false;

        toastr.success(
          'If this email exists, reset link has been sent.',
          '',
          { timeOut: 2500 }
        );

        this.backToEmailStep();

      }, err => {
        this.loading = false;
        toastr.error('Oops! Something went wrong', '', { timeOut: 1000 });
      });
  }
  showSignUp() {
    this.loginStep = 'signUp';
    this.signUpSubmitted = false;
    this.errorMessage = '';
    this.accountNotFound = false;
    this.loginForm.setErrors(null);

    this.signUpForm.reset({
      name: '',
      surname: '',
      email: this.loginForm.value.email || '',
      segmentId: 0,
      subSegmentId: 0,
      ssubSegmentId: 0
    });

    this.loadSignUpStaticData();
  }

  loadSignUpStaticData() {
    this.reqRes.getData('/auth/get-sign-up-static-data').subscribe(data => {
      this.segmentObj = data.segmentObj || [];
    }, error => {
      toastr.error('Oops! Something went wrong', '', { timeOut: 1000 });
    });
  }

  signUpSegmentChange(segmentId) {
    this.subSegmentObj = [];
    this.ssubSegmentObj = [];

    this.signUpForm.patchValue({
      subSegmentId: 0,
      ssubSegmentId: 0
    });

    if (!segmentId || segmentId == 0) return;

    this.reqRes.getData('/auth/get-sign-up-subsegment/' + segmentId).subscribe(data => {
      this.subSegmentObj = data || [];
    }, error => {
      toastr.error('Oops! Something went wrong', '', { timeOut: 1000 });
    });
  }

  signUpSubSegmentChange(subSegmentId) {
    this.ssubSegmentObj = [];

    this.signUpForm.patchValue({
      ssubSegmentId: 0
    });

    if (!subSegmentId || subSegmentId == 0) return;

    this.reqRes.getData('/auth/get-sign-up-ssubsegment/' + subSegmentId).subscribe(data => {
      this.ssubSegmentObj = data || [];
    }, error => {
      toastr.error('Oops! Something went wrong', '', { timeOut: 1000 });
    });
  }

  submitSignUp() {
    this.signUpSubmitted = true;
    this.errorMessage = '';

    if (
      this.signUpForm.invalid ||
      this.signUpForm.value.segmentId == 0 ||
      this.signUpForm.value.subSegmentId == 0 ||
      this.signUpForm.value.ssubSegmentId == 0
    ) {
      return;
    }

    this.loading = true;

    this.authService.signUpRequest(this.signUpForm.value)
      .pipe(first())
      .subscribe(data => {
        this.loading = false;

        if (!data.success) {
          toastr.error(
            data.error || 'Request could not be submitted.',
            '',
            { timeOut: 2500 }
          );
          return;
        }

        toastr.success(
          data.message || 'Your sign up request has been submitted successfully. IOSC support team will review your information and contact you after your account is created.',
          '',
          { timeOut: 4000 }
        );

        this.backToEmailStep();

      }, error => {
        this.loading = false;
        toastr.error('Oops! Something went wrong', '', { timeOut: 1000 });
      });
  }
}
